import { OnboardingLayout } from "@/components/layouts/OnboardingLayout"
import { Input } from "@/components/ui/input"
import { Map as MapIcon } from "lucide-react"
import React from "react"
import { Small } from "@/components/ui/typography"
import { useForm, useWatch } from "react-hook-form"
import { AddressType, addressSchema } from "@/state/draft/hqAddress"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormField, FormItem, FormMessage } from "@/components/ui/form"
import { AddressAutocomplete } from "../../primitives/AddressAutocomplete"
import { RHFProvider } from "@/components/forms/RHFProvider"
import { useOSM } from "@/features/map/useOSM"
import { OSMapComponent } from "@/features/map/OSMapComponent"
import { fromLatLon } from "@/features/map/fromLatLon"

type OnboardingAddressScreenProps = {
	value: AddressType
	onSubmit: (value: AddressType) => void
	onSaveState: (value: AddressType) => void
	onBack: () => void
}

export const OnboardingAddressScreen: React.FC<OnboardingAddressScreenProps> = (
	props
) => {
	const form = useForm<AddressType>({
		defaultValues: props.value,
		resolver: zodResolver(addressSchema),
	})

	const values = useWatch({
		control: form.control,
		defaultValue: props.value,
	}) as AddressType

	React.useEffect(() => {
		props.onSaveState(values)
	}, [values])

	const hqAddr = useOSM({
		initialView: {
			center: fromLatLon([
				values.latlon[0] || 36.7783,
				values.latlon[1] || -119.4179,
			]),
			zoom: 15,
		},
		onMarkerDragEnd: (e) => {},
		defaultAddress: values.address,
		onSelectedPlaceChange: (place) => {
			form.setValue("address", place.displayName)
			form.setValue("latlon", [place.lat, place.lon])
		},
	})

	return (
		<OnboardingLayout
			stepper={{
				currentStep: 3,
				steps: 4,
			}}
			title="Select the start address"
			description="Select the address where you typically begin your business trips"
			onNext={() => {
				form.handleSubmit((data) => {
					props.onSubmit(data)
				})()
			}}
			onBack={() => {
				props.onBack()
			}}
		>
			<RHFProvider form={form}>
				<div className="flex flex-col gap-1.5">
					<Small className="text-xs text-muted-foreground leading-5 font-normal">
						Usually, this is your office or home address.
					</Small>
					<div>
						<FormField
							name="address"
							control={form.control}
							render={({ field, fieldState }) => {
								return (
									<AddressAutocomplete
										suggestions={hqAddr.autocomplete.suggestions}
										onSuggestionSelect={hqAddr.selectPlace}
										ref={hqAddr.autocomplete.сontainerRef}
									>
										<Input
											{...field}
											autoComplete="off"
											type="text"
											placeholder="Enter your start address"
											className=""
											endIcon={
												<MapIcon
													size={16}
													className="text-muted-foreground"
												/>
											}
											value={hqAddr.autocomplete.input.value}
											onChange={(e) => {
												hqAddr.autocomplete.input.onChange(
													e.target.value
												)
												field.onChange(e)
											}}
										/>
										{!!fieldState.error && (
											<FormMessage>
												Please enter a valid address
											</FormMessage>
										)}
									</AddressAutocomplete>
								)
							}}
						/>
					</div>
					<div className="h-[220px] relative">
						<OSMapComponent
							view={[hqAddr.view, hqAddr.setView]}
							marker={hqAddr.marker}
							initial={hqAddr.initial}
						/>
					</div>
				</div>
			</RHFProvider>
		</OnboardingLayout>
	)
}
